import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import MediaCard from "@/components/MediaCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tv, Radio, Search, SlidersHorizontal, Loader2, BadgeCheck } from "lucide-react";

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const { t } = useTranslation();
  const [provinces, setProvinces] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");

  const type = params.get("type") || "";
  const province = params.get("province") || "";
  const language = params.get("language") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "popular";
  const certified = params.get("certified") === "true";

  useEffect(() => {
    api.get("/provinces").then((r) => setProvinces(r.data));
    api.get("/languages").then((r) => setLanguages(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const setParam = useCallback((key, val) => {
    const next = new URLSearchParams(params);
    if (val && val !== "all") next.set(key, val); else next.delete(key);
    setParams(next);
  }, [params, setParams]);

  const load = useCallback(async () => {
    setLoading(true);
    const sp = new URLSearchParams();
    if (type) sp.set("type", type);
    if (province) sp.set("province", province);
    if (language) sp.set("language", language);
    if (category) sp.set("category", category);
    if (params.get("q")) sp.set("q", params.get("q"));
    if (certified) sp.set("certified", "true");
    sp.set("sort", sort);
    sp.set("limit", "48");
    try {
      const { data } = await api.get(`/media?${sp.toString()}`);
      setData(data);
    } finally { setLoading(false); }
  }, [type, province, language, category, sort, certified, params]);

  useEffect(() => { load(); }, [load]);

  const title = type === "tv" ? t("browse.tv") : type === "radio" ? t("browse.radio") : t("browse.all_media");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      <div className="flex items-center gap-2 mb-1">
        {type === "tv" ? <Tv className="text-rdcblue" /> : type === "radio" ? <Radio className="text-rdcgold" /> : <SlidersHorizontal className="text-primary" />}
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">{title}</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-5">{t("browse.count", { n: data.total })}</p>

      {/* Type segment */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[["", t("browse.all")], ["tv", t("nav.tv")], ["radio", t("nav.radio")]].map(([v, l]) => (
          <button key={v} data-testid={`filter-type-${v || "all"}`} onClick={() => setParam("type", v || "all")}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${type === v ? "bg-primary text-white" : "bg-secondary text-secondary-foreground hover:bg-border"}`}>
            {l}
          </button>
        ))}
        <button
          data-testid="filter-certified"
          onClick={() => setParam("certified", certified ? "" : "true")}
          title={t("browse.certified_hint")}
          className={`sm:ml-auto inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${certified ? "bg-rdcgold text-amber-950" : "bg-secondary text-secondary-foreground hover:bg-border"}`}>
          <BadgeCheck size={16} /> {t("browse.certified_only")}
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-6">
        <div className="relative col-span-2 sm:flex-1 sm:min-w-52">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <input
            data-testid="browse-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setParam("q", q)}
            placeholder={t("common.search")}
            className="w-full rounded-lg border border-border bg-card pl-9 pr-3 py-2 text-sm outline-none focus:border-primary"
          />
        </div>
        <Select value={province || "all"} onValueChange={(v) => setParam("province", v)}>
          <SelectTrigger className="w-full sm:w-40" data-testid="filter-province-select"><SelectValue placeholder={t("browse.province")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("browse.all_provinces")}</SelectItem>{provinces.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={language || "all"} onValueChange={(v) => setParam("language", v)}>
          <SelectTrigger className="w-full sm:w-36" data-testid="filter-language-select"><SelectValue placeholder={t("browse.language")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("browse.all_languages")}</SelectItem>{languages.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={category || "all"} onValueChange={(v) => setParam("category", v)}>
          <SelectTrigger className="w-full sm:w-40" data-testid="filter-category-select"><SelectValue placeholder={t("browse.category")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("browse.all_categories")}</SelectItem>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
          <SelectTrigger className="w-full sm:w-36" data-testid="filter-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="popular">{t("browse.sort_popular")}</SelectItem>
            <SelectItem value="live">{t("browse.sort_live")}</SelectItem>
            <SelectItem value="recent">{t("browse.sort_recent")}</SelectItem>
            <SelectItem value="name">{t("browse.sort_name")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" size={32} /></div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="browse-empty">{t("browse.empty")}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="browse-grid">
          {data.items.map((m, i) => <MediaCard key={m.id} media={m} index={i} />)}
        </div>
      )}
    </div>
  );
}
